import { useMemo } from 'react';
import { FileCode2 } from 'lucide-react';
import { diffFileNotes, diffFilePath, diffFileSymlink, parseUnifiedDiff, type DiffFile } from '@shared/patch-request';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

export interface PatchDiffProps {
  /** A unified diff (git diff, or diff -u): one or more files. */
  diff: string;
  /** Show only this file (its index among the diff's files), as the patch request dialog's file list picks. */
  only?: number;
  /** Shown when it has no files. */
  emptyText?: string;
  className?: string;
}

/**
 * A unified diff as it will be applied, file by file: each file's path, whether it's new or deleted,
 * its added and removed line counts, every header line that changes more than content (a mode, a
 * rename, a symlink), and its hunks with old and new line numbers, + and − in the gutter. Lines git
 * can't place show as they are, so nothing in the patch is hidden.
 */
export function PatchDiff({ diff, only, emptyText = 'The patch has no files.', className }: PatchDiffProps) {
  const parsed = useMemo(() => parseUnifiedDiff(diff), [diff]);
  const files = only === undefined ? parsed : parsed.slice(only, only + 1);
  if (!files.length) return <p className={cn('text-sm text-muted-foreground', className)}>{emptyText}</p>;
  return (
    <div className={cn('space-y-3', className)}>
      {files.map((file, i) => (
        <PatchDiffFile key={`${diffFilePath(file)}:${i}`} file={file} />
      ))}
    </div>
  );
}

function PatchDiffFile({ file }: { file: DiffFile }) {
  const path = diffFilePath(file);
  const renamed = file.oldPath && file.newPath && file.oldPath !== file.newPath;
  const notes = diffFileNotes(file);
  const symlink = diffFileSymlink(file);
  return (
    <section className="overflow-hidden rounded-md border" aria-label={path}>
      <header className="flex flex-wrap items-center gap-x-2 gap-y-1 border-b bg-muted px-3 py-1.5 text-xs">
        <FileCode2 className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
        <span className="min-w-0 break-all font-mono font-medium">{renamed ? `${file.oldPath} → ${file.newPath}` : path}</span>
        {file.oldPath === null ? (
          <Badge size="sm" variant="success">
            New file
          </Badge>
        ) : null}
        {file.newPath === null ? (
          <Badge size="sm" variant="danger">
            Deleted
          </Badge>
        ) : null}
        {renamed ? (
          <Badge size="sm" variant="info">
            Renamed
          </Badge>
        ) : null}
        {symlink ? (
          <Badge size="sm" variant="warning">
            Symlink
          </Badge>
        ) : null}
        {file.binary ? (
          <Badge size="sm" variant="muted">
            Binary
          </Badge>
        ) : null}
        <span className="ml-auto shrink-0 font-mono tabular-nums">
          <span className="text-success-strong">+{file.added}</span> <span className="text-danger-strong">−{file.removed}</span>
        </span>
      </header>
      {notes.length ? (
        <ul className="border-b bg-warning-soft px-3 py-1 font-mono text-2xs text-warning-strong" aria-label="Header changes">
          {notes.map((note, n) => (
            <li key={n} className="break-all">
              {note}
            </li>
          ))}
        </ul>
      ) : null}
      {file.hunks.length ? (
        <div className="overflow-x-auto font-mono text-xs leading-5">
          {file.hunks.map((hunk, h) => (
            <div key={h}>
              <div className="bg-info-soft px-3 py-0.5 text-2xs whitespace-pre text-info-strong">{hunk.header}</div>
              {hunk.lines.map((line, l) => (
                <div
                  key={l}
                  className={cn(
                    'flex min-w-max whitespace-pre',
                    line.kind === 'add' && 'bg-success-soft text-success-strong',
                    line.kind === 'remove' && 'bg-danger-soft text-danger-strong',
                    line.kind === 'note' && 'text-muted-foreground italic'
                  )}
                >
                  <span className="w-10 shrink-0 pr-2 text-right text-muted-foreground select-none">{line.oldLine ?? ''}</span>
                  <span className="w-10 shrink-0 pr-2 text-right text-muted-foreground select-none">{line.newLine ?? ''}</span>
                  <span className="w-4 shrink-0 select-none" aria-hidden>
                    {line.kind === 'add' ? '+' : line.kind === 'remove' ? '−' : ''}
                  </span>
                  <span className="sr-only">{line.kind === 'add' ? 'Added: ' : line.kind === 'remove' ? 'Removed: ' : ''}</span>
                  <span className="pr-3">{line.text || ' '}</span>
                </div>
              ))}
            </div>
          ))}
        </div>
      ) : (
        <p className="px-3 py-2 text-xs text-muted-foreground">
          {file.binary ? "A binary file: its contents can't be shown." : 'No line changes.'}
        </p>
      )}
    </section>
  );
}
